import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, MailCheck, MailX } from 'lucide-react'
import { KaryvaLogoAnimated3D } from '../components/KaryvaLogoAnimated3D'
import { useTranslation } from '../i18n/I18nContext'
import { useAuth } from '../contexts/AuthContext'
import { emailVerificationService } from '../services/emailVerification'

/**
 * Sprint 30 — public verify-email page, reached from the emailed link
 * (`/verify-email?token=…`). Consumes the token on mount.
 *
 * Public route: the link may open in a logged-out browser, so failure
 * copy points at "sign in and resend" rather than assuming a session.
 */
export default function VerifyEmailPage() {
  const { t } = useTranslation()
  const { user, refreshUser } = useAuth()
  const [state, setState] = useState<'verifying' | 'success' | 'error'>('verifying')
  // StrictMode double-mounts effects in dev; the token is single-use, so
  // the second POST would 400 and flash the failure state.
  const fired = useRef(false)

  useEffect(() => {
    if (fired.current) return
    fired.current = true

    const token = new URLSearchParams(window.location.search).get('token') ?? ''
    if (!token) {
      setState('error')
      return
    }
    emailVerificationService
      .verifyEmail(token)
      .then(async () => {
        setState('success')
        // If a session is live, pull the fresh email_verified flag so the
        // banner is gone when they continue into the app.
        await refreshUser()
      })
      .catch(() => setState('error'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#102a43] via-[#1a3a52] to-[#102a43] flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-center">
          <KaryvaLogoAnimated3D size="header" darkBg={true} />
        </div>

        <div className="bg-[#1d293d] rounded-[14px] border border-[#314158] p-6 md:p-8 shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.3)]">
          {state === 'verifying' && (
            <div className="space-y-4 text-center">
              <Loader2 className="size-10 text-[#DAA520] mx-auto animate-spin" />
              <h1 className="font-['Arial:Regular',sans-serif] text-xl text-white">
                {t('auth.verify.verifying')}
              </h1>
            </div>
          )}

          {state === 'success' && (
            <div className="space-y-4 text-center">
              <MailCheck className="size-10 text-[#DAA520] mx-auto" />
              <h1 className="font-['Arial:Regular',sans-serif] text-xl text-white">
                {t('auth.verify.successTitle')}
              </h1>
              <p className="font-['Arial:Regular',sans-serif] text-sm leading-[22px] text-[#cad5e2]">
                {t('auth.verify.successBody')}
              </p>
              <Link
                to={user ? '/' : '/login'}
                className="inline-block text-sm text-[#51a2ff] hover:text-[#73b5ff] transition-colors"
              >
                {user ? t('auth.verify.goToApp') : t('auth.verify.backToLogin')}
              </Link>
            </div>
          )}

          {state === 'error' && (
            <div className="space-y-4 text-center">
              <MailX className="size-10 text-[#fca5a5] mx-auto" />
              <h1 className="font-['Arial:Regular',sans-serif] text-xl text-white">
                {t('auth.verify.failTitle')}
              </h1>
              <p className="font-['Arial:Regular',sans-serif] text-sm leading-[22px] text-[#cad5e2]">
                {t('auth.verify.failBody')}
              </p>
              <Link
                to={user ? '/' : '/login'}
                className="inline-block text-sm text-[#51a2ff] hover:text-[#73b5ff] transition-colors"
              >
                {user ? t('auth.verify.goToApp') : t('auth.verify.backToLogin')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
